import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { downloadCsv, reportFilename } from './reports/reportUtils';

// ─── Shared helpers ──────────────────────────────────────────────────────────

const g = (row, ...keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null) return String(v);
  }
  return '';
};

const readRaw = (row, ...keys) => {
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(row || {}, k)) {
      return row[k];
    }
  }
  return undefined;
};

const normalizeName = (value) => String(value || '').trim().replace(/\s+/g, ' ');

const splitName = (row) => {
  const first = normalizeName(g(row, 'firstName', 'First Name'));
  const last = normalizeName(g(row, 'lastName', 'Last Name'));
  if (first || last) return { first, last };

  const full = normalizeName(g(row, 'Name', 'name'));
  if (!full) return { first: '', last: '' };
  const parts = full.split(' ');
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
};

const fullName = (row) => {
  const { first, last } = splitName(row);
  return [first, last].filter(Boolean).join(' ').trim();
};

const firstNameForSort = (row) => splitName(row).first.toLowerCase();

const sortByFirstNameAsc = (a, b) => {
  const firstCmp = firstNameForSort(a).localeCompare(firstNameForSort(b));
  if (firstCmp !== 0) return firstCmp;
  return fullName(a).toLowerCase().localeCompare(fullName(b).toLowerCase());
};

const YES_TOKENS = new Set(['yes', 'true', 'y', '1', 'on']);
const NO_TOKENS = new Set(['no', 'false', 'n', '0', 'off']);

const getLeadingToken = (value) => {
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) return '';
  const match = s.match(/^([a-z0-9]+)/);
  return match ? match[1] : s;
};

const isExplicitTrueValue = (value) => {
  if (value === true || value === 1) return true;
  const token = getLeadingToken(value);
  return token ? YES_TOKENS.has(token) : false;
};

const isExplicitFalseValue = (value) => {
  if (value === false || value === 0) return true;
  const token = getLeadingToken(value);
  return token ? NO_TOKENS.has(token) : false;
};

const hasExplicitYes = (row, keys) => keys.some((key) => isExplicitTrueValue(readRaw(row, key)));

const trailingDetail = (value) => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const colonIx = s.indexOf(':');
  if (colonIx >= 0) return s.slice(colonIx + 1).trim();
  const dashIx = s.indexOf('-');
  if (dashIx >= 0 && isExplicitTrueValue(s.slice(0, dashIx))) return s.slice(dashIx + 1).trim();
  return '';
};

const formatAllergyInfo = (row) => {
  const raw = readRaw(row, 'Alllergies', 'Allergies', 'hasAllergies');
  if (!isExplicitTrueValue(raw)) return '';
  const details =
    trailingDetail(raw) ||
    String(readRaw(row, 'allergies', 'allergyDetails', 'allergiesDetails', 'allergy') || '').trim();
  return details || 'Yes';
};

const formatMedicationInfo = (row) => {
  const raw = readRaw(row, 'Medications', 'takesMedications', 'takesMedication');
  if (!isExplicitTrueValue(raw)) return '';
  const details =
    trailingDetail(raw) ||
    String(readRaw(row, 'medications', 'meds', 'medicationDetails') || '').trim();
  return details || 'Yes';
};

const hasAllergyYes = (row) => hasExplicitYes(row, ['Alllergies', 'Allergies', 'hasAllergies']);
const hasMedicationYes = (row) => hasExplicitYes(row, ['Medications', 'takesMedications', 'takesMedication']);

const isSponsorshipSelected = (row) => {
  if (hasExplicitYes(row, ['sponsorshipRequested', 'needsAssistance', 'Needs Assistance'])) return true;

  const sponsorshipRequested = row?.sponsorship?.requested;
  if (isExplicitTrueValue(sponsorshipRequested)) return true;

  const details =
    String(readRaw(row, 'Sponsorship Details', 'sponsorshipDetails') || '').trim() ||
    String(row?.sponsorship?.details || '').trim() ||
    String(row?.sponsorship?.reason || '').trim();
  if (details) return true;

  const payment = String(readRaw(row, 'Payment', 'payment') || '').toLowerCase();
  return payment.includes('sponsor');
};

const stayingOnGroundsYesNo = (row) => {
  const explicit = readRaw(
    row,
    'Staying on Ground',
    'Staying on Grounds',
    'stayingOnGround',
    'stayingOnGrounds',
    'stayOption'
  );

  if (isExplicitTrueValue(explicit)) return 'Yes';
  if (isExplicitFalseValue(explicit)) return 'No';

  const text = String(
    explicit ??
    readRaw(
      row,
      'Staying on Grounds/Rooming with others',
      'roomingWithOthers',
      'Dorm Choice'
    ) ??
    ''
  )
    .toLowerCase()
    .trim();

  if (!text) return 'No';

  if (
    text.includes('dorm') ||
    text.includes('cabin') ||
    text.includes('rv') ||
    text.includes('motel') ||
    text.includes('with-parent') ||
    text.includes('parent')
  ) {
    return 'Yes';
  }

  if (text.includes('off') || text.includes('commute')) return 'No';

  return 'No';
};

const registrationDisplayName = (row) => fullName(row) || normalizeName(g(row, 'Name', 'name'));

const normalizeEmailKey = (value) => String(value || '').trim().toLowerCase();
const normalizePersonNameKey = (value) => normalizeName(value).toLowerCase();

const roomLabelFromAssignment = (a) => {
  if (!a) return 'Unassigned';
  const area = normalizeName(a.area);
  const room = normalizeName(a.room);
  const bed = normalizeName(a.bed);
  const parts = [area, room, bed].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'Assigned';
};

const REPORT_IDS = [
  'master',
  'allergies',
  'medications',
  'sponsored',
  'emergency',
  'rooming',
  'nurse_incidents',
  'financials',
];

const REPORT_LABELS = {
  master: 'Master Registration',
  allergies: 'Allergies List',
  medications: 'Medications List',
  sponsored: 'Sponsorship',
  emergency: 'Emergency Contact List',
  rooming: 'Room Assignments / Rooming',
  nurse_incidents: 'Nurse — Incident Log',
  financials: 'Financials',
};

// ─── Reusable sub-components ─────────────────────────────────────────────────

function ReportTable({ columns, rows, emptyMsg }) {
  if (!rows || rows.length === 0) {
    return <div className="muted" style={{ marginTop: 12 }}>{emptyMsg || 'No data.'}</div>;
  }
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table
        className="report-table"
        style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
      >
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key || c.label}
                style={{
                  textAlign: 'left',
                  padding: '6px 8px',
                  borderBottom: '2px solid #ddd',
                  whiteSpace: 'nowrap',
                  fontWeight: 700,
                  background: '#f8fafc',
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
              {columns.map((c) => {
                const val = typeof c.get === 'function' ? c.get(row) : row[c.key];
                return (
                  <td key={c.key || c.label} style={{ padding: '5px 8px', verticalAlign: 'top' }}>
                    {val ?? ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportActions({ onPrint, onCsv, loading }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
      <button
        type="button"
        className="btn"
        onClick={onPrint}
        disabled={loading}
        style={{ minHeight: 36, padding: '6px 14px' }}
      >
        🖨 Print
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={onCsv}
        disabled={loading}
        style={{ minHeight: 36, padding: '6px 14px' }}
      >
        ⬇ Download CSV
      </button>
    </div>
  );
}

function CardReportLayout({ rows, emptyMsg, renderCard }) {
  if (!rows.length) {
    return <div className="muted" style={{ marginTop: 12 }}>{emptyMsg}</div>;
  }

  return <div className="report-card-grid">{rows.map((row, i) => renderCard(row, i))}</div>;
}

function PersonCard({ title, rows }) {
  return (
    <div className="master-print-card">
      <div className="master-print-name">{title || 'Unnamed registrant'}</div>
      {rows.map((item) => (
        <div className="master-print-row" key={item.label}>
          <strong>{item.label}:</strong> {item.value || '—'}
        </div>
      ))}
    </div>
  );
}

// ─── Report: Master Registration ──────────────────────────────────────────────

const MASTER_COLS = [
  { label: 'First Name', get: (r) => splitName(r).first },
  { label: 'Last Name', get: (r) => splitName(r).last },
  { label: 'DOB', get: (r) => g(r, 'DOB', '_dob') },
  { label: 'Email', get: (r) => g(r, 'email', 'Email') },
  { label: 'Phone', get: (r) => g(r, 'Phone Number', 'phone', 'Phone') },
  { label: 'Sponsorship Chosen', get: (r) => (isSponsorshipSelected(r) ? 'Yes' : 'No') },
  { label: 'Staying on Grounds', get: (r) => stayingOnGroundsYesNo(r) },
];

function MasterReport({ registrations }) {
  const sortedRows = useMemo(() => [...registrations].sort(sortByFirstNameAsc), [registrations]);
  const handleCsv = () => downloadCsv(sortedRows, MASTER_COLS, reportFilename('master-registration'));
  const handlePrint = () => window.print();

  return (
    <div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />
      <CardReportLayout
        rows={sortedRows}
        emptyMsg="No registrations found."
        renderCard={(r, i) => {
          const { first, last } = splitName(r);
          return (
            <PersonCard
              key={`${registrationDisplayName(r)}-${i}`}
              title={registrationDisplayName(r)}
              rows={[
                { label: 'First name', value: first },
                { label: 'Last name', value: last },
                { label: 'DOB', value: g(r, 'DOB', '_dob') },
                { label: 'Email', value: g(r, 'email', 'Email') },
                { label: 'Phone', value: g(r, 'Phone Number', 'phone', 'Phone') },
                { label: 'Sponsorship chosen', value: isSponsorshipSelected(r) ? 'Yes' : 'No' },
                { label: 'Staying on grounds', value: stayingOnGroundsYesNo(r) },
              ]}
            />
          );
        }}
      />
    </div>
  );
}

// ─── Report: Allergies List ───────────────────────────────────────────────────

const ALLERGY_COLS = [
  { label: 'First Name', get: (r) => splitName(r).first },
  { label: 'Last Name', get: (r) => splitName(r).last },
  { label: 'DOB', get: (r) => g(r, 'DOB', '_dob') },
  { label: 'Allergy Information', get: (r) => formatAllergyInfo(r) || '' },
  { label: 'Emergency1 Name', get: (r) => g(r, 'Emergency1 Name') },
  { label: 'Emergency1 Number', get: (r) => g(r, 'Emergency1 Number') },
  { label: 'Emergency2 Name', get: (r) => g(r, 'Emergency2 Name') },
  { label: 'Emergency2 Number', get: (r) => g(r, 'Emergency2 Number') },
];

function AllergiesReport({ registrations }) {
  const rows = useMemo(
    () => [...registrations.filter((r) => hasAllergyYes(r))].sort(sortByFirstNameAsc),
    [registrations]
  );
  const handleCsv = () => downloadCsv(rows, ALLERGY_COLS, reportFilename('allergies-list'));
  const handlePrint = () => window.print();

  return (
    <div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Showing {rows.length} registrant(s) with an explicit allergies yes/true value.
      </div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />
      <CardReportLayout
        rows={rows}
        emptyMsg="No allergy records found."
        renderCard={(r, i) => (
          <PersonCard
            key={`${registrationDisplayName(r)}-${i}`}
            title={registrationDisplayName(r)}
            rows={[
              { label: 'First name', value: splitName(r).first },
              { label: 'Last name', value: splitName(r).last },
              { label: 'DOB', value: g(r, 'DOB', '_dob') },
              { label: 'Allergy information', value: formatAllergyInfo(r) },
              { label: 'Emergency contact 1', value: `${g(r, 'Emergency1 Name') || '—'} ${g(r, 'Emergency1 Number') ? `(${g(r, 'Emergency1 Number')})` : ''}`.trim() },
              { label: 'Emergency contact 2', value: `${g(r, 'Emergency2 Name') || '—'} ${g(r, 'Emergency2 Number') ? `(${g(r, 'Emergency2 Number')})` : ''}`.trim() },
            ]}
          />
        )}
      />
    </div>
  );
}

// ─── Report: Medications List ─────────────────────────────────────────────────

const MEDS_COLS = [
  { label: 'First Name', get: (r) => splitName(r).first },
  { label: 'Last Name', get: (r) => splitName(r).last },
  { label: 'DOB', get: (r) => g(r, 'DOB', '_dob') },
  { label: 'Medication Information', get: (r) => formatMedicationInfo(r) || '' },
  { label: 'Emergency1 Name', get: (r) => g(r, 'Emergency1 Name') },
  { label: 'Emergency1 Number', get: (r) => g(r, 'Emergency1 Number') },
  { label: 'Emergency2 Name', get: (r) => g(r, 'Emergency2 Name') },
  { label: 'Emergency2 Number', get: (r) => g(r, 'Emergency2 Number') },
];

function MedicationsReport({ registrations }) {
  const rows = useMemo(
    () => [...registrations.filter((r) => hasMedicationYes(r))].sort(sortByFirstNameAsc),
    [registrations]
  );
  const handleCsv = () => downloadCsv(rows, MEDS_COLS, reportFilename('medications-list'));
  const handlePrint = () => window.print();

  return (
    <div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Showing {rows.length} registrant(s) with an explicit medications yes/true value.
      </div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />
      <CardReportLayout
        rows={rows}
        emptyMsg="No medication records found."
        renderCard={(r, i) => (
          <PersonCard
            key={`${registrationDisplayName(r)}-${i}`}
            title={registrationDisplayName(r)}
            rows={[
              { label: 'First name', value: splitName(r).first },
              { label: 'Last name', value: splitName(r).last },
              { label: 'DOB', value: g(r, 'DOB', '_dob') },
              { label: 'Medication information', value: formatMedicationInfo(r) },
              { label: 'Emergency contact 1', value: `${g(r, 'Emergency1 Name') || '—'} ${g(r, 'Emergency1 Number') ? `(${g(r, 'Emergency1 Number')})` : ''}`.trim() },
              { label: 'Emergency contact 2', value: `${g(r, 'Emergency2 Name') || '—'} ${g(r, 'Emergency2 Number') ? `(${g(r, 'Emergency2 Number')})` : ''}`.trim() },
            ]}
          />
        )}
      />
    </div>
  );
}

// ─── Report: Sponsored ────────────────────────────────────────────────────────

const SPONSORED_COLS = [
  { label: 'First Name', get: (r) => splitName(r).first },
  { label: 'Last Name', get: (r) => splitName(r).last },
  { label: 'DOB', get: (r) => g(r, 'DOB', '_dob') },
  { label: 'Email', get: (r) => g(r, 'email', 'Email') },
  { label: 'Phone Number', get: (r) => g(r, 'Phone Number', 'phone', 'Phone') },
  { label: 'Sponsorship Details', get: (r) => g(r, 'Sponsorship Details', 'sponsorshipDetails', 'Payment') },
];

function SponsoredReport({ registrations }) {
  const rows = useMemo(
    () => [...registrations.filter((r) => isSponsorshipSelected(r))].sort(sortByFirstNameAsc),
    [registrations]
  );
  const handleCsv = () => downloadCsv(rows, SPONSORED_COLS, reportFilename('sponsored-only'));
  const handlePrint = () => window.print();

  return (
    <div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Showing {rows.length} registrant(s) with sponsorship selected.
      </div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />
      <CardReportLayout
        rows={rows}
        emptyMsg="No sponsored registrations found."
        renderCard={(r, i) => (
          <PersonCard
            key={`${registrationDisplayName(r)}-${i}`}
            title={registrationDisplayName(r)}
            rows={[
              { label: 'DOB', value: g(r, 'DOB', '_dob') },
              { label: 'Email', value: g(r, 'email', 'Email') },
              { label: 'Phone', value: g(r, 'Phone Number', 'phone', 'Phone') },
              { label: 'Sponsorship details', value: g(r, 'Sponsorship Details', 'sponsorshipDetails', 'Payment') || 'Requested' },
            ]}
          />
        )}
      />
    </div>
  );
}

// ─── Report: Emergency Contact List ──────────────────────────────────────────
// CHANGED:
// - Removed Medication Information from this report
// - Added Room Assignment (resolved from admin assignments)

function EmergencyReport({ registrations, token }) {
  const rows = useMemo(() => [...registrations].sort(sortByFirstNameAsc), [registrations]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) return;
      try {
        const res = await fetch('/.netlify/functions/admin-get-assignments', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const raw = await res.text();
        let json = {};
        try { json = raw ? JSON.parse(raw) : {}; } catch { json = {}; }
        if (!cancelled && res.ok) {
          setAssignments(Array.isArray(json.rows) ? json.rows : []);
        }
      } catch {
        if (!cancelled) setAssignments([]);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token]);

  const assignmentLookup = useMemo(() => {
    const byEmail = {};
    const byName = {};

    assignments.forEach((a) => {
      const emailKey = normalizeEmailKey(a.camperEmail);
      const nameKey = normalizePersonNameKey(a.camperName);
      if (emailKey && !byEmail[emailKey]) byEmail[emailKey] = a;
      if (nameKey && !byName[nameKey]) byName[nameKey] = a;
    });

    return { byEmail, byName };
  }, [assignments]);

  const getAssignment = useCallback((r) => {
    const emailKey = normalizeEmailKey(g(r, 'email', 'Email'));
    const nameKey = normalizePersonNameKey(registrationDisplayName(r));
    return assignmentLookup.byEmail[emailKey] || assignmentLookup.byName[nameKey] || null;
  }, [assignmentLookup]);

  const EMERGENCY_COLS = useMemo(() => ([
    { label: 'First Name', get: (r) => splitName(r).first },
    { label: 'Last Name', get: (r) => splitName(r).last },
    { label: 'Room Assignment', get: (r) => roomLabelFromAssignment(getAssignment(r)) },
    { label: 'DOB', get: (r) => g(r, 'DOB', '_dob') },
    { label: 'Phone Number', get: (r) => g(r, 'Phone Number', 'phone', 'Phone') },
    { label: 'Emergency1 Name', get: (r) => g(r, 'Emergency1 Name') },
    { label: 'Emergency1 Number', get: (r) => g(r, 'Emergency1 Number') },
    { label: 'Emergency2 Name', get: (r) => g(r, 'Emergency2 Name') },
    { label: 'Emergency2 Number', get: (r) => g(r, 'Emergency2 Number') },
    { label: 'Allergy Information', get: (r) => formatAllergyInfo(r) },
  ]), [getAssignment]);

  const handleCsv = () => downloadCsv(rows, EMERGENCY_COLS, reportFilename('emergency-contact-list'));
  const handlePrint = () => window.print();

  return (
    <div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />
      <CardReportLayout
        rows={rows}
        emptyMsg="No registrations found."
        renderCard={(r, i) => {
          const assignment = getAssignment(r);
          return (
            <PersonCard
              key={`${registrationDisplayName(r)}-${i}`}
              title={registrationDisplayName(r)}
              rows={[
                { label: 'Room assignment', value: roomLabelFromAssignment(assignment) },
                { label: 'DOB', value: g(r, 'DOB', '_dob') },
                { label: 'Phone', value: g(r, 'Phone Number', 'phone', 'Phone') },
                { label: 'Emergency contact 1', value: `${g(r, 'Emergency1 Name') || '—'} ${g(r, 'Emergency1 Number') ? `(${g(r, 'Emergency1 Number')})` : ''}`.trim() },
                { label: 'Emergency contact 2', value: `${g(r, 'Emergency2 Name') || '—'} ${g(r, 'Emergency2 Number') ? `(${g(r, 'Emergency2 Number')})` : ''}`.trim() },
                { label: 'Allergy information', value: formatAllergyInfo(r) || '—' },
              ]}
            />
          );
        }}
      />
    </div>
  );
}

// ─── Report: Rooming Roster ───────────────────────────────────────────────────

const ROOMING_COLS = [
  { label: 'Area', get: (r) => r.area },
  { label: 'Room', get: (r) => r.room },
  { label: 'Door code', get: (r) => r.doorCode },
  { label: 'Name', get: (r) => r.name },
  { label: 'Paid', get: (r) => (r.paid ? 'Yes' : 'No') },
  { label: 'Checked In', get: (r) => (r.checkedIn ? 'Yes' : 'No') },
  { label: 'Sponsorship', get: (r) => (r.sponsorship ? 'Yes' : 'No') },
  { label: 'Payment / Group Info', get: (r) => r.paymentGroupInfo },
];

const ROOM_AREA_ORDER = ['Motel', 'RV', 'Cabin', 'Girls Dorm', 'Boys Dorm'];

const normalizeArea = (area) => {
  const text = String(area || '').trim().toLowerCase();
  if (!text) return 'Unassigned';
  if (text.includes('motel')) return 'Motel';
  if (text.includes('rv')) return 'RV';
  if (text.includes('cabin')) return 'Cabin';
  if (text.includes('girl') && text.includes('dorm')) return 'Girls Dorm';
  if (text.includes('boy') && text.includes('dorm')) return 'Boys Dorm';
  return normalizeName(area);
};

const sortRoomName = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

const isCheckedIn = (row) => hasExplicitYes(row, ['checkedIn', 'checkIn', 'checked_in']);

const paymentOrGroupInfo = (registration, assignmentRow) => {
  const items = [];
  const payment = String(readRaw(registration || {}, 'Payment', 'payment') || '').trim();
  const notes = String(assignmentRow?.notes || '').trim();

  if (isSponsorshipSelected(registration || {})) items.push('Sponsorship requested');
  if (payment && /youth\s*group|group/i.test(payment)) items.push(payment);
  if (notes && /youth\s*group|group/i.test(notes)) items.push(notes);

  return items.join(' • ');
};

function RoomingReport({ token, registrations }) {
  const [assignments, setAssignments] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');

  const fetchAssignments = useCallback(async () => {
    if (!token) return;
    setAssignLoading(true);
    setAssignError('');
    try {
      const res = await fetch('/.netlify/functions/admin-get-assignments', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
      if (!res.ok) {
        setAssignError(json?.error || `Failed to load assignments: ${res.status}`);
        return;
      }
      setAssignments(Array.isArray(json.rows) ? json.rows : []);
    } catch {
      setAssignError('Network error loading assignments.');
    } finally {
      setAssignLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const registrationLookup = useMemo(() => {
    const byEmail = {};
    const byName = {};

    registrations.forEach((r) => {
      const email = String(g(r, 'email', 'Email')).trim().toLowerCase();
      const name = registrationDisplayName(r).toLowerCase();
      if (email && !byEmail[email]) byEmail[email] = r;
      if (name && !byName[name]) byName[name] = r;
    });

    return { byEmail, byName };
  }, [registrations]);

  const grouped = useMemo(() => {
    const areaRoomMap = {};

    assignments.forEach((a) => {
      const area = normalizeArea(a.area);
      const room = normalizeName(a.room) || 'Unassigned';
      const doorCode = normalizeName(a.doorCode);
      const camperName = normalizeName(a.camperName) || 'Unknown';
      const email = normalizeName(a.camperEmail).toLowerCase();

      const reg = registrationLookup.byEmail[email] || registrationLookup.byName[camperName.toLowerCase()] || null;
      const paid = String(a.paid || '').toLowerCase() === 'true';
      const checkedIn = isCheckedIn(a);

      if (!areaRoomMap[area]) areaRoomMap[area] = {};
      if (!areaRoomMap[area][room]) {
        areaRoomMap[area][room] = { doorCode: doorCode || '', people: [] };
      }
      if (doorCode && !areaRoomMap[area][room].doorCode) areaRoomMap[area][room].doorCode = doorCode;

      areaRoomMap[area][room].people.push({
        key: a.id || `${area}-${room}-${camperName}-${email}`,
        name: camperName,
        paid,
        checkedIn,
        sponsorship: isSponsorshipSelected(reg || {}),
        paymentGroupInfo: paymentOrGroupInfo(reg, a),
      });
    });

    Object.values(areaRoomMap).forEach((rooms) => {
      Object.values(rooms).forEach((entry) => {
        entry.people.sort((a, b) => a.name.localeCompare(b.name));
      });
    });

    return areaRoomMap;
  }, [assignments, registrationLookup]);

  const flatRows = useMemo(() => {
    const rows = [];
    Object.entries(grouped).forEach(([area, rooms]) => {
      Object.entries(rooms).forEach(([room, roomData]) => {
        roomData.people.forEach((person) => {
          rows.push({
            area,
            room,
            doorCode: roomData.doorCode || '',
            name: person.name,
            paid: person.paid,
            checkedIn: person.checkedIn,
            sponsorship: person.sponsorship,
            paymentGroupInfo: person.paymentGroupInfo || '',
          });
        });
      });
    });
    return rows.sort((a, b) => `${a.area} ${a.room}`.localeCompare(`${b.area} ${b.room}`, undefined, { numeric: true }));
  }, [grouped]);

  const allAreas = useMemo(() => {
    const extras = Object.keys(grouped).filter((a) => !ROOM_AREA_ORDER.includes(a)).sort();
    return [...ROOM_AREA_ORDER, ...extras];
  }, [grouped]);

  const handleCsv = () => downloadCsv(flatRows, ROOMING_COLS, reportFilename('rooming-roster'));
  const handlePrint = () => window.print();

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {assignLoading && <span className="muted" style={{ fontSize: 12 }}>Loading assignments…</span>}
        {assignError && <span className="error" style={{ fontSize: 12 }}>{assignError}</span>}
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Showing {flatRows.length} assigned camper(s).
      </div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />

      <div className="rooming-area-grid">
        {allAreas.map((area) => {
          const roomMap = grouped[area] || {};
          const roomNames = Object.keys(roomMap).sort(sortRoomName);

          return (
            <section key={area} className="rooming-area-section">
              <h4 className="rooming-area-title">{area}</h4>
              {roomNames.length === 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>No rooms assigned.</div>
              ) : (
                <div className="rooming-room-grid">
                  {roomNames.map((roomName) => {
                    const room = roomMap[roomName];
                    const paidCount = room.people.filter((p) => p.paid).length;

                    return (
                      <div key={`${area}-${roomName}`} className="master-print-card">
                        <div className="master-print-name">{roomName}</div>
                        <div className="master-print-row"><strong>Door code:</strong> {room.doorCode || '—'}</div>
                        <div className="master-print-row"><strong>Paid count:</strong> {paidCount}/{room.people.length}</div>

                        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                          {room.people.map((person) => (
                            <div key={person.key} className="rooming-person-row">
                              <div style={{ fontWeight: 700 }}>{person.name}</div>
                              <div className="rooming-person-status">
                                Paid: {person.paid ? '☑' : '☐'} • Checked in: {person.checkedIn ? '☑' : '☐'}
                              </div>
                              {person.paymentGroupInfo ? (
                                <div className="rooming-person-note">{person.paymentGroupInfo}</div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ─── Report: Nurse — Incident Log ─────────────────────────────────────────────

const INCIDENT_COLS = [
  { label: 'Date/Time', get: (r) => g(r, 'incidenteAt', 'incidentAt', 'createdAt') },
  { label: 'Camper Name', get: (r) => [g(r, 'camperFirst'), g(r, 'camperLast')].filter(Boolean).join(' ') },
  { label: 'DOB', get: (r) => g(r, 'dob') },
  { label: 'Category', get: (r) => g(r, 'category') },
  { label: 'Location', get: (r) => g(r, 'location') },
  { label: 'Description', get: (r) => g(r, 'description') },
  { label: 'Action Taken', get: (r) => g(r, 'actionTaken') },
  { label: 'Guardian Notified', get: (r) => g(r, 'guardianNotified') },
  { label: 'Notes', get: (r) => g(r, 'notes') },
];

function NurseIncidentsReport({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchIncidents = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/admin-get-incidents', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = await res.text();
      let json = {};
      try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
      if (!res.ok) {
        setError(json?.error || `Failed to load incidents: ${res.status}`);
        return;
      }
      setRows(Array.isArray(json.rows) ? json.rows : []);
    } catch {
      setError('Network error loading incidents.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  const handleCsv = () => downloadCsv(rows, INCIDENT_COLS, reportFilename('nurse-incident-log'));
  const handlePrint = () => window.print();

  if (loading) return <div className="muted" style={{ marginTop: 12 }}>Loading incidents…</div>;

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} loading={loading} />
      <ReportTable columns={INCIDENT_COLS} rows={rows} emptyMsg="No incidents recorded." />
      {/* TODO: Add Meds Given log report once med_logs data source is confirmed accessible for admin role. */}
    </div>
  );
}

// ─── Report: Financials ───────────────────────────────────────────────────────

const FIN_COLS = [
  { label: 'Type', get: (r) => r.type },
  { label: 'Category', get: (r) => r.category },
  { label: 'Who Paid', get: (r) => r.whoPaid },
  { label: 'Amount', get: (r) => r.amount },
  { label: 'Date', get: (r) => r.date },
  { label: 'Description', get: (r) => r.description },
  { label: 'Method', get: (r) => r.method },
];

const normalizeFinanceType = (row) => {
  const type = String(readRaw(row, 'type', 'Type') || '').trim().toLowerCase();
  if (type === 'income') return 'Income';
  if (type === 'expense') return 'Expense';
  return type ? type[0].toUpperCase() + type.slice(1) : 'Unknown';
};

const normalizeFinanceCategory = (row, normalizedType) => {
  const type = String(normalizedType || '').toLowerCase();
  const raw = String(readRaw(row, 'category', 'Category') || '').trim();
  const lower = raw.toLowerCase();

  if (lower.includes('registration')) return 'Registration';
  if (lower.includes('donation')) return 'Donations';
  if (lower.includes('concession') && lower.includes('dining')) return 'Concessions (Dining Hall)';
  if (lower.includes('concession') && lower.includes('sideboard')) return 'Concessions (Sideboard)';
  if (lower.includes('utilities')) return 'Utilities';
  if (lower.includes('suppl')) return 'Supplies';
  if (lower.includes('food')) return 'Food';
  if (lower.includes('marketing')) return 'Marketing';
  if (lower.includes('scholar')) return 'Scholarships';
  if (lower.includes('staff')) return 'Staff';

  if (type === 'income') return 'Other Income';
  if (type === 'expense') return 'Other Expenses';

  return raw || 'Other';
};

const parseFinanceAmount = (row) => {
  const n = Number(String(readRaw(row, 'amount', 'Amount') || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const formatWhoPaid = (row, category, registrationByEmail) => {
  const paidBy = String(readRaw(row, 'paidBy', 'PaidBy', 'paid_by') || '').trim();
  if (paidBy) return paidBy;

  if (String(category).toLowerCase() !== 'registration') return '';

  const noteText = [readRaw(row, 'notes', 'Notes'), readRaw(row, 'description', 'Description')]
    .filter(Boolean)
    .map((x) => String(x))
    .join(' ');

  const emailMatch = noteText.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  const email = emailMatch ? emailMatch[0].toLowerCase() : '';
  if (email && registrationByEmail[email]) return registrationDisplayName(registrationByEmail[email]) || email;

  return email || '';
};

function FinancialsReport({ financeData, registrations }) {
  const registrationByEmail = useMemo(() => {
    const map = {};
    registrations.forEach((r) => {
      const email = String(g(r, 'email', 'Email')).trim().toLowerCase();
      if (email && !map[email]) map[email] = r;
    });
    return map;
  }, [registrations]);

  const rows = useMemo(() => {
    const rawRows = Array.isArray(financeData?.rows) ? financeData.rows : [];
    return rawRows
      .map((row) => {
        const type = normalizeFinanceType(row);
        const category = normalizeFinanceCategory(row, type);
        const amount = parseFinanceAmount(row);
        return {
          type,
          category,
          whoPaid: formatWhoPaid(row, category, registrationByEmail),
          amount: amount.toFixed(2),
          date: String(readRaw(row, 'date', 'Date', 'createdAt', 'created_at') || '').trim(),
          description: String(readRaw(row, 'description', 'Description') || '').trim(),
          method: String(readRaw(row, 'method', 'Method') || '').trim(),
        };
      })
      .sort((a, b) => {
        const dateCmp = String(b.date).localeCompare(String(a.date));
        if (dateCmp !== 0) return dateCmp;
        return a.category.localeCompare(b.category);
      });
  }, [financeData, registrationByEmail]);

  const handleCsv = () => downloadCsv(rows, FIN_COLS, reportFilename('financials'));
  const handlePrint = () => window.print();

  return (
    <div>
      <ReportActions onPrint={handlePrint} onCsv={handleCsv} />
      <ReportTable columns={FIN_COLS} rows={rows} emptyMsg="No finance data." />
    </div>
  );
}

// ─── Main AdminReports component ──────────────────────────────────────────────

export default function AdminReports({ token, registrations, financeData }) {
  const [activeReport, setActiveReport] = useState('master');
  const regs = Array.isArray(registrations) ? registrations : [];

  return (
    <div style={{ marginTop: 12 }}>
      <h2 style={{ margin: '0 0 12px 0' }}>Reports</h2>

      {/* Report selector */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          marginBottom: 18,
          padding: '10px',
          background: '#f8fafc',
          borderRadius: 10,
          border: '1px solid #e2e8f0',
        }}
        className="no-print"
      >
        {REPORT_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={`btn ${activeReport === id ? 'primary' : ''}`}
            style={{ minHeight: 32, padding: '4px 12px', fontSize: 12 }}
            onClick={() => setActiveReport(id)}
          >
            {REPORT_LABELS[id]}
          </button>
        ))}
      </div>

      {/* Active report title */}
      <h3 style={{ margin: '0 0 8px 0', fontSize: 16 }}>{REPORT_LABELS[activeReport]}</h3>

      {/* Report content */}
      <div id="print-area">
        {activeReport === 'master' && <MasterReport registrations={regs} />}
        {activeReport === 'allergies' && <AllergiesReport registrations={regs} />}
        {activeReport === 'medications' && <MedicationsReport registrations={regs} />}
        {activeReport === 'sponsored' && <SponsoredReport registrations={regs} />}
        {activeReport === 'emergency' && <EmergencyReport registrations={regs} token={token} />}
        {activeReport === 'rooming' && <RoomingReport token={token} registrations={regs} />}
        {activeReport === 'nurse_incidents' && <NurseIncidentsReport token={token} />}
        {activeReport === 'financials' && <FinancialsReport financeData={financeData} registrations={regs} />}
      </div>
    </div>
  );
}

export {
  isExplicitTrueValue,
  hasAllergyYes,
  hasMedicationYes,
  isSponsorshipSelected,
  normalizeFinanceCategory,
};